import { Clipboard } from '@angular/cdk/clipboard';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy } from '@angular/core';
import { PortalService } from '@vsolv/packages/portal-config/web';
import { StorageItem } from '@vsolv/packages/storage/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { PaginationConfig } from '@vsolv/vectors/table';
import { SecurityService } from '@wsphere/staff/web';
import { Claim } from '@wsphere/warranties/domain';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  merge,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { ClaimService } from '../../services';

@Component({
  selector: 'ws-claim-attachments',
  templateUrl: './claim-attachments.page.html',
  styleUrls: ['./claim-attachments.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ClaimAttachmentsPage implements OnDestroy {
  private http = inject(HttpClient);
  private toastSvc = inject(ToastService);
  private claimSvc = inject(ClaimService);
  private clipboardCdk = inject(Clipboard);
  private portalSvc = inject(PortalService);
  private securitySvc = inject(SecurityService);
  private changeDetector = inject(ChangeDetectorRef);

  protected claim$ = this.claimSvc.getClaim();

  private permissionKey$ = this.claim$.pipe(
    map(claim => (claim?.warranty?.distributor?.permissionKey ? [claim?.warranty?.distributor?.permissionKey] : null)),
    shareReplay(1)
  );

  protected canManage$ = this.permissionKey$.pipe(
    switchMap(key => this.securitySvc.hasAccess('clm_ManageAttachments', key)),
    shareReplay(1)
  );

  private refresh$ = new Subject<void>();

  protected deletingItemId: string | null = null;
  protected expandedItem$ = new BehaviorSubject<StorageItem.Model | null>(null);

  protected loading$ = new BehaviorSubject(false);
  protected pagination$ = new BehaviorSubject({ limit: 10, page: 1 });

  private data$ = this.claim$.pipe(
    distinctUntilChanged((p, n) => p?.id === n?.id),
    switchMap(claim => this.pagination$.pipe(map(pagination => ({ ...pagination, claim })))),

    tap(() => {
      this.loading$.next(true);
      this.expand(null);
    }),

    switchMap(({ claim, ...params }) => {
      if (!claim) return of(null);

      // Uploads from anywhere on the page (hold evidence, notes) reload the list too.
      const uploaded$ = this.claimSvc.documentsChanged$.pipe(filter(claimId => claimId === claim.id));

      return merge(this.refresh$, uploaded$).pipe(
        startWith(null),
        switchMap(() =>
          this.http.get<Claim.Documents.ListClaimDocumentsResponse>(`/api/claims/${claim.id}/documents`, {
            params: {
              page: params.page ?? 1,
              limit: params.limit ?? 10,
            },
          })
        )
      );
    }),

    catchError(() =>
      of(<Claim.Documents.ListClaimDocumentsResponse>{
        meta: {
          itemsPerPage: 10,

          itemCount: 0,
          currentPage: 1,

          totalItems: 0,
          totalPages: 0,
        },
        items: [],
      })
    ),
    tap(() => this.loading$.next(false)),
    shareReplay(1)
  );

  protected meta$ = this.data$.pipe(map(data => data?.meta ?? null));

  protected attachments$ = this.data$.pipe(
    map(data => data?.items ?? []),
    tap(() => setTimeout(() => this.changeDetector.markForCheck()))
  );

  protected paginationConfig$ = this.meta$.pipe(
    startWith(null),
    map(meta => {
      return <PaginationConfig>{
        itemsPerPage: meta?.itemsPerPage ?? 10,
        currentPage: meta?.currentPage ?? 1,
        totalItems: meta?.totalItems ?? 0,
        pageSizes: [10, 20, 50],
      };
    })
  );

  protected pages$ = combineLatest([this.meta$, this.pagination$]).pipe(
    startWith([null, null]),
    map(([meta, pagination]) => {
      const total = meta?.totalPages ?? 1;
      if (total < 3) return new Array(total).fill(null).map((_, i) => i + 1);

      const page = pagination?.page ?? 1;

      if (page === 1) {
        return [1, 2, 3];
      } else if (page === total) {
        return [total - 2, total - 1, total];
      } else {
        return [page - 1, page, page + 1];
      }
    }),
    distinctUntilChanged((p, n) => JSON.stringify(p ?? []) === JSON.stringify(n ?? []))
  );

  protected linkButtonInfo$ = this.claim$.pipe(
    map(claim => {
      if (!claim?.warranty) return null;

      return [
        {
          text: 'Copy inspection report request link',
          title: 'Inspection report',
          description: `This is the inspection report for ${claim.warranty.contractNumber}`,
        },
        {
          text: 'Copy receipt request link',
          title: 'Receipt',
          description: `This is the receipt for ${claim.warranty.contractNumber}`,
        },
        {
          text: 'Copy supporting evidence request link ',
          title: 'Supporting evidence',
          description: `This is supporting evidence for claim: ${claim.warranty.contractNumber}. It is used to better understand the complaint.`,
        },
      ];
    })
  );

  protected trackById(_: number, item: StorageItem.Model) {
    return item.id;
  }

  protected refresh() {
    this.refresh$.next();
  }

  protected expand(item: StorageItem.Model | null) {
    const current = this.expandedItem$.value;
    this.expandedItem$.next(item === null || item.id === current?.id ? null : item);

    this.changeDetector.markForCheck();
  }

  protected async deleteItem() {
    const claimId = await firstValueFrom(this.claim$.pipe(map(claim => claim?.id ?? null)));
    if (!claimId || !this.deletingItemId) return;

    try {
      this.loading$.next(true);

      await this.claimSvc.removeDocument(claimId, this.deletingItemId);
      this.refresh();

      this.toastSvc.show({
        type: 'success',
        title: 'Document deleted',
        text: 'Document deleted and removed from the claim.',
      });
    } catch {
      this.loading$.next(false);

      this.toastSvc.show({
        type: 'error',
        title: 'Unable to delete document',
        text: 'Unable to delete document. Please try again.',
      });
    } finally {
      this.deletingItemId = null;
    }
  }

  protected async copyLinkToClipboard(link: { title: string }) {
    const claim = await firstValueFrom(this.claim$);
    if (!claim?.warranty) return;

    const portalLink = await this.portalSvc.getPortalUrl();
    const attachmentData = encodeURIComponent(JSON.stringify(link)).replace(/'/g, '%27');

    this.clipboardCdk.copy(
      `${portalLink.url}/warranty/${claim.warranty.contractNumber}/claim/${claim.id}/manage-attachments?claimId=${claim.id}&openDialog=true&addAttachmentData=${attachmentData}`
    );

    this.toastSvc.show({
      type: 'success',
      title: `Link copied to clipboard.`,
      text: `The <strong>${link.title}</strong> link has been copied to your clipboard and can now be sent out to customers.`,
    });
  }

  ngOnDestroy() {
    this.refresh$.complete();
    this.loading$.complete();
    this.pagination$.complete();
    this.expandedItem$.complete();
  }
}
